import { useMemo, useState } from "react";
import {
  useGetUsersQuery,
  useDeleteUserMutation,
} from "graphql/__generated__/hooks";
import {
  deleteUserThunk,
  resetPasswordRequestThunk,
} from "reduxStore/auth/thunks";
import { useAppDispatch } from "hooks/useAppDispatch";
import {
  Messages,
  NotificationSeverity,
  TABLE_ACTIONS,
} from "helpers/constants";
import { ROLES, User } from "types/user";
import { showNotificationThunk } from "reduxStore/alert/thunks";
import { useNotifications } from "hooks/useNotifications";
import { useNavigate } from "react-router-dom";

export const useUserList = () => {
  const { loading, error, data, refetch } = useGetUsersQuery();
  const dispatch = useAppDispatch();
  const [deleteUser] = useDeleteUserMutation();
  const [createOrEditUserModal, setCreateOrEditUserModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | undefined>(undefined);
  const [deleteUserModal, setDeleteUserModal] = useState(false);
  const [inviteUserModal, setInviteUserModal] = useState(false);
  const [provisionCognitoModal, setProvisionCognitoModal] = useState(false);
  const [syncCognitoAttributesModal, setSyncCognitoAttributesModal] =
    useState(false);
  const [userToDelete, setUserToDelete] = useState<User | undefined>(undefined);
  const users = useMemo(() => {
    if (!data?.user) return [];
    const organizationsById = new Map(
      data.organizations.map((organization) => [
        organization.org_id,
        organization,
      ]),
    );

    return data.user.map((user) => {
      const memberships = user.organization.map((org) => org.organization);
      const legacyOrganization = user.org_id
        ? organizationsById.get(user.org_id)
        : undefined;
      const organization =
        memberships.length > 0
          ? memberships
          : legacyOrganization
            ? [legacyOrganization]
            : [];

      return {
        ...user,
        organization,
      };
    }) as User[];
  }, [data]);
  const { notify } = useNotifications();
  const navigate = useNavigate();
  const userCounter = useMemo(() => {
    return {
      total: users.length,
      admins: users.filter((user) => user.role === ROLES.ADMIN).length,
      managers: users.filter((user) => user.role === ROLES.MANAGER).length,
      directors: users.filter((user) => user.role === ROLES.DIRECTOR).length,
      coaches: users.filter((user) => user.role === ROLES.COACH).length,
      students: users.filter((user) => user.role === ROLES.STUDENT).length,
    };
  }, [users]);

  const handleDelete = async (userToDelete: User) => {
    const { payload } = await dispatch(deleteUserThunk(userToDelete.email));
    if (payload === "success") {
      await deleteUser({
        variables: { id: userToDelete.user_id },
      });
      notify({
        message: Messages.successUserDeleted,
        severity: NotificationSeverity.success,
      });
      await refetch();
    } else {
      notify({
        message: Messages.errorUserDeleted,
        severity: NotificationSeverity.error,
      });
    }
  };

  const handleCreateOrUpdateUser: (user?: User) => void = async () => {
    if (createOrEditUserModal) setCreateOrEditUserModal(false);
    setTimeout(async () => {
      await refetch();
    }, 300);
  };

  const handleActionClick = async (action: TABLE_ACTIONS, id: string) => {
    const userToEdit = users?.find((user) => user.user_id === id);
    switch (action) {
      case TABLE_ACTIONS.EMAIL_INVITE:
        setInviteUserModal(true);
        setUserToEdit(userToEdit);
        break;
      case TABLE_ACTIONS.EDIT:
        if (userToEdit) {
          // handleCreateOrUpdateUserModalOpen(true, userToEdit);
          navigate(`/user/${id}/edit`);
        }
        break;
      case TABLE_ACTIONS.DELETE:
        setUserToDelete(userToEdit);
        setDeleteUserModal(true);
        break;
      case TABLE_ACTIONS.VIEW_FULL_PROFILE:
        navigate(`/user/${userToEdit?.user_id}`);
        break;
      default:
        break;
    }
  };
  const sendInvitation = async (email: string) => {
    await dispatch(
      resetPasswordRequestThunk({ email, isResendingInvite: true }),
    );
    dispatch(
      showNotificationThunk({
        message: "Invitation sent successfully",
        severity: NotificationSeverity.success,
      }),
    );
  };

  const handleCreateOrUpdateUserModalOpen = (
    isEditing?: boolean,
    userToEdit?: User | undefined,
  ) => {
    setIsEditing(!!isEditing);
    setUserToEdit(userToEdit);
    setCreateOrEditUserModal(true);
  };

  const handleCreateUserModalDismiss = () => {
    setCreateOrEditUserModal(false);
  };
  return {
    loading,
    error,
    users,
    handleActionClick,
    handleDelete,
    createOrEditUserModal,
    setCreateOrEditUserModal,
    handleCreateOrUpdateUser,
    isEditing,
    userToEdit,
    setUserToEdit,
    deleteUserModal,
    setDeleteUserModal,
    userToDelete,
    setUserToDelete,
    handleCreateOrUpdateUserModalOpen,
    handleCreateUserModalDismiss,
    userCounter,
    inviteUserModal,
    setInviteUserModal,
    sendInvitation,
    provisionCognitoModal,
    setProvisionCognitoModal,
    syncCognitoAttributesModal,
    setSyncCognitoAttributesModal,
  };
};
